import React, { useState, useRef, useEffect } from 'react';

/**
 * InfoIcon Component - A reusable info icon that displays a tooltip popup on click
 * @param {string} tooltipText - The text to display in the popup
 * @param {string} position - Position of the popup: 'top', 'bottom', 'left', 'right' (default: 'right')
 */
const InfoIcon = ({ tooltipText, position = 'right' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [popupMaxWidth, setPopupMaxWidth] = useState(400);
  const iconRef = useRef(null);
  const popupRef = useRef(null);

  // Calculate max width based on parent container
  useEffect(() => {
    if (isVisible && iconRef.current) {
      // Find the parent container (traverse up to find a suitable container)
      let parentElement = iconRef.current.parentElement;
      let foundWidth = 400; // Default to 400 if no suitable parent found

      while (parentElement) {
        const width = parentElement.offsetWidth;

        // Look for a parent with substantial width (at least 300px)
        if (width >= 300) {
          foundWidth = width - 40; // Leave some margin
          break;
        }
        parentElement = parentElement.parentElement;
      }

      // Set maxWidth between 250px and 400px
      const maxWidth = Math.max(250, Math.min(400, foundWidth))-50;
      setPopupMaxWidth(maxWidth);
    }
  }, [isVisible]);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        iconRef.current &&
        !iconRef.current.contains(event.target) &&
        popupRef.current &&
        !popupRef.current.contains(event.target)
      ) {
        setIsVisible(false);
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible]);

  // Close popup when any key is pressed
  useEffect(() => {
    const handleKeyPress = (event) => {
      setIsVisible(false);
    };

    if (isVisible) {
      document.addEventListener('keydown', handleKeyPress);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isVisible]);

  const togglePopup = (e) => {
    e.stopPropagation();
    setIsVisible(!isVisible);
  };

  const getPopupPosition = () => {
    const positions = {
      right: {
        left: '100%',
        top: '50%',
        transform: 'translate(-10%, -150%)',
      },
      left: {
        right: '100%',
        top: '50%',
        transform: 'translate(-8px, -50%)',
      },
      top: {
        bottom: '100%',
        left: '50%',
        transform: 'translate(-100%, -100%)',
      },
      bottom: {
        top: '100%',
        left: '50%',
        transform: 'translate(-50%, 8px)',
      },
    };
    return positions[position] || positions.right;
  };

  return (
    <span
      style={{
        position: 'relative',
        display: 'inline-block',
        marginLeft: '6px',
        marginRight: '0px'
      }}
    >
      <span
        ref={iconRef}
        onClick={togglePopup}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          border: '1px solid #8b949e',
          color: '#8b949e',
          fontSize: '9px',
          fontWeight: 'bold',
          cursor: 'pointer',
          userSelect: 'none',
          transition: 'all 0.2s ease',
          paddingRight: '1px',
          paddingLeft: '0px',
          paddingBottom: '1px'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#8b949e';
          e.currentTarget.style.color = '#0d1117';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
          e.currentTarget.style.color = '#8b949e';
        }}
      >
        i
      </span>

      {isVisible && (
        <span
          ref={popupRef}
          style={{
            position: 'absolute',
            ...getPopupPosition(),
            backgroundColor: '#161b22',
            border: '1px solid #FFFFFF',
            borderRadius: '6px',
            padding: '8px 12px',
            color: '#c9d1d9',
            fontSize: '11px',
            lineheight: '1.3',
            width: `${popupMaxWidth}px`,
            maxWidth: `${popupMaxWidth}px`,
            whiteSpace: 'normal',
            zIndex: 1000,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)',
            fontFamily: "'Courier New', Courier, monospace",
          }}
        >
          {tooltipText}
        </span>
      )}
    </span>
  );
};

export default InfoIcon;
